import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Loader2, Search, X } from "@fg/icons";
import { connectPostsApi, type ConversationScopeKind, type ConversationSearchHit } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";

function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Plain text with every case-insensitive match of `q` wrapped in <mark>. Text only — never HTML. */
export function Highlighted({ text, q }: { text: string; q: string }) {
    const parts = useMemo(() => {
        const needle = q.trim();
        if (!needle) return [text];
        return text.split(new RegExp(`(${escapeRegExp(needle)})`, "gi"));
    }, [text, q]);
    const needle = q.trim().toLowerCase();
    return (
        <>
            {parts.map((part, i) =>
                needle && part.toLowerCase() === needle ? (
                    <mark key={i} className="rounded-sm bg-amber-200 px-0.5 text-foreground dark:bg-amber-500/40">{part}</mark>
                ) : (
                    <Fragment key={i}>{part}</Fragment>
                )
            )}
        </>
    );
}

/**
 * Find in this conversation (Teams' Ctrl+F in a chat or channel). Queries the
 * BFF, which decrypts and scans the conversation's history in pages; results
 * show a highlighted plain-text snippet, and choosing one jumps to that
 * message (the thread loads the messages around it when it isn't loaded).
 * Enter / Shift+Enter step through the results, Esc closes.
 */
export function ConversationFindBar({ scope, id, label, onJump, onClose }: {
    scope: ConversationScopeKind;
    id: string;
    /** "#general", "your chat with Ann" — for the search box's label. */
    label: string;
    onJump: (hit: ConversationSearchHit) => void;
    onClose: () => void;
}) {
    const token = useAccessToken();
    const inputRef = useRef<HTMLInputElement>(null);
    const [text, setText] = useState("");
    const [q, setQ] = useState("");
    const [active, setActive] = useState(-1);

    useEffect(() => { inputRef.current?.focus(); }, []);
    useEffect(() => {
        const t = window.setTimeout(() => setQ(text.trim()), 300);
        return () => window.clearTimeout(t);
    }, [text]);
    useEffect(() => { setActive(-1); }, [q]);

    const search = useInfiniteQuery({
        queryKey: ["conversation-search", scope, id, q, token],
        queryFn: ({ pageParam }) => connectPostsApi.searchConversation(scope, id, q, pageParam, token),
        initialPageParam: null as string | null,
        getNextPageParam: (last) => last.nextBefore ?? undefined,
        enabled: Boolean(token && q.length >= 2),
        staleTime: 10_000
    });
    const hits = useMemo(() => search.data?.pages.flatMap((p) => p.hits) ?? [], [search.data]);
    const scanned = search.data?.pages.reduce((n, p) => n + p.scanned, 0) ?? 0;
    const searching = q.length >= 2;

    const pick = (i: number) => {
        const hit = hits[i];
        if (!hit) return;
        setActive(i);
        onJump(hit);
    };
    const step = (delta: 1 | -1) => {
        if (hits.length === 0) return;
        pick(active < 0 ? (delta === 1 ? 0 : hits.length - 1) : (active + delta + hits.length) % hits.length);
    };

    const status = !searching
        ? ""
        : search.isPending
            ? "Searching…"
            : hits.length === 0
                ? (search.hasNextPage ? `No matches in the latest ${scanned} messages` : "No messages match")
                : `${active >= 0 ? `${active + 1} of ` : ""}${hits.length}${search.hasNextPage ? "+" : ""} ${hits.length === 1 ? "result" : "results"}`;

    return (
        <div role="search" aria-label={`Search in ${label}`} className="shrink-0 border-b bg-muted/30 px-3 py-2">
            <div className="flex items-center gap-2">
                <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <input
                    ref={inputRef}
                    type="search"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            e.preventDefault();
                            onClose();
                        } else if (e.key === "Enter") {
                            e.preventDefault();
                            // Search right away rather than waiting for the debounce.
                            if (text.trim() !== q) setQ(text.trim());
                            else step(e.shiftKey ? -1 : 1);
                        }
                    }}
                    placeholder={`Find in ${label}`}
                    aria-label={`Find in ${label}`}
                    maxLength={200}
                    className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline" aria-hidden>{status}</span>
                <button
                    type="button"
                    onClick={() => step(-1)}
                    disabled={hits.length === 0}
                    aria-label="Previous result"
                    title="Previous (Shift+Enter)"
                    className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent disabled:opacity-40"
                >
                    <ChevronUp className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => step(1)}
                    disabled={hits.length === 0}
                    aria-label="Next result"
                    title="Next (Enter)"
                    className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent disabled:opacity-40"
                >
                    <ChevronDown className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close search"
                    title="Close (Esc)"
                    className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
            <p className="sr-only" aria-live="polite">{status}</p>

            {searching ? (
                <div className="mt-2 max-h-72 overflow-y-auto rounded-md border bg-card">
                    {search.isPending ? (
                        <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Searching…
                        </div>
                    ) : search.isError ? (
                        <p className="px-3 py-3 text-sm text-destructive">
                            {search.error instanceof Error && !search.error.message.startsWith("Connect BFF")
                                ? search.error.message
                                : "Couldn't search this conversation. Try again."}
                        </p>
                    ) : hits.length === 0 && !search.hasNextPage ? (
                        <p className="px-3 py-3 text-sm text-muted-foreground">No messages match “{q}”.</p>
                    ) : null}
                    {hits.length > 0 ? (
                        <ul aria-label="Search results">
                            {hits.map((h, i) => (
                                <li key={h.messageId} className="border-b last:border-b-0">
                                    <button
                                        type="button"
                                        onClick={() => pick(i)}
                                        aria-current={i === active ? "true" : undefined}
                                        className={cn(
                                            "block w-full px-3 py-2 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none",
                                            i === active && "bg-accent"
                                        )}
                                    >
                                        <span className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-muted-foreground">
                                            <span className="font-semibold text-foreground">{h.authorName}</span>
                                            <time dateTime={h.sentAt}>
                                                {new Date(h.sentAt).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}
                                            </time>
                                            {h.parentMessageId ? <span>· reply in a thread</span> : null}
                                        </span>
                                        {h.subject ? (
                                            <span className="block text-sm font-semibold"><Highlighted text={h.subject} q={q} /></span>
                                        ) : null}
                                        <span className="line-clamp-2 break-words text-sm"><Highlighted text={h.snippet} q={q} /></span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    {search.hasNextPage ? (
                        <button
                            type="button"
                            onClick={() => void search.fetchNextPage()}
                            disabled={search.isFetchingNextPage}
                            className="flex w-full items-center justify-center gap-1.5 border-t px-3 py-2 text-xs font-medium text-primary hover:bg-accent disabled:opacity-60"
                        >
                            {search.isFetchingNextPage ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
                            Search older messages
                        </button>
                    ) : hits.length > 0 ? (
                        <p className="border-t px-3 py-1.5 text-center text-[11px] text-muted-foreground">Searched the whole conversation</p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
